/**
 * Outcomes — slice 60. What "Converted" means in a workspace, the ledger that
 * records conversions, and the funnel that reads it.
 *
 * ## TWO TIERS, AND THE TIER TRAVELS WITH EVERY FIGURE
 *
 * The owner's concept names four attribution tiers — matched, coded, correlated,
 * declared — "each tagged with how much to trust it". Slice 60 builds the two
 * that need no integration:
 *
 *   - **matched** — a known person converted on a known day. Recorded by
 *     uploading a list of who converted, matched to people by email or phone
 *     (slice 58). Joinable to the campaigns that messaged them.
 *   - **declared** — a count the workspace typed in for a period. No person,
 *     so never attributed to a campaign. The lowest tier.
 *
 * A figure built from one tier is never added to a figure from the other: the
 * funnel's Converted stage is matched people only, and the declared total is
 * shown beside it under its own name. Adding them would give a number that is
 * partly about people and partly about a typed count, which is a number about
 * nothing.
 *
 * ## WEND-SENT ATTRIBUTION, AND ITS WINDOW
 *
 * A matched conversion belongs to a channel when a message on that channel was
 * accepted for that person in the {@link ATTRIBUTION_WINDOW_DAYS} days before
 * it (up to the end of the day it happened). It is the concept's "Wend-sent"
 * model: good for "did our message precede this?", blind to everything Wend did
 * not send. Holdouts (slice 61) are what make it causal.
 *
 * ## MONEY
 *
 * Integer minor units (paise) and a currency, INR only — as `metaMoneySchema`
 * and 0042 do. Never a float.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { channelSchema } from '../enums.js';
import { calendarDateSchema } from './analytics.js';

export const OUTCOME_TIER_VALUES = ['matched', 'declared'] as const;
export const outcomeTierSchema = z.enum(OUTCOME_TIER_VALUES);
export type OutcomeTier = z.infer<typeof outcomeTierSchema>;

/** The tier as a person reads it, beside every figure built from it. */
export const OUTCOME_TIER_LABEL: Readonly<Record<OutcomeTier, string>> = {
  matched: 'Matched',
  declared: 'Declared',
};

/** Days before a conversion in which a message on a channel earns it. */
export const ATTRIBUTION_WINDOW_DAYS = 7;
/** The funnel's window, in days. */
export const FUNNEL_WINDOW_DAYS = 30;

/** Most rows one uploaded list may hold. A list larger than this is two uploads. */
export const OUTCOME_UPLOAD_MAX_ROWS = 20_000;
/** Most money one outcome type may be worth, in paise (₹1,00,00,00,000). */
export const OUTCOME_VALUE_MAX_MINOR = 100_000_000_000;

/* -------------------------------------------------------------------------- */
/* Outcome types                                                               */
/* -------------------------------------------------------------------------- */

export const outcomeTypeSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    valueMinor: z.number().int().min(0),
    currency: z.literal('INR'),
    /**
     * Slice 64: a WhatsApp quick-reply payload that means this conversion. A
     * person who presses that button gets a Matched outcome of this type.
     */
    waReplyPayload: z.string().nullable(),
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type OutcomeType = z.infer<typeof outcomeTypeSchema>;

const outcomeTypeNameSchema = z
  .string()
  .trim()
  .min(1, 'Give it a name, like "Order" or "Loan enquiry".')
  .max(80, 'Keep the name under 80 characters.');
const valueMinorSchema = z
  .number()
  .int('Type rupees with at most two decimal places.')
  .min(0, 'A value cannot be negative.')
  .max(OUTCOME_VALUE_MAX_MINOR, 'Up to ₹1,00,00,00,000.');

/**
 * The payload exactly as the template's author set it on the button — Meta
 * sends it back byte for byte, so it is compared as typed, case included.
 * Empty clears it.
 */
const waReplyPayloadSchema = z
  .string()
  .trim()
  .max(256, 'A button payload is at most 256 characters.')
  .transform((value) => (value === '' ? null : value))
  .nullable();

export const createOutcomeTypeRequestSchema = z
  .object({ name: outcomeTypeNameSchema, valueMinor: valueMinorSchema, waReplyPayload: waReplyPayloadSchema.optional() })
  .strict();
export type CreateOutcomeTypeRequest = z.infer<typeof createOutcomeTypeRequestSchema>;

/**
 * A type may be renamed or repriced. Repricing never rewrites what outcomes
 * already recorded were worth — each ledger row carries its own value (0042).
 */
export const updateOutcomeTypeRequestSchema = z
  .object({
    name: outcomeTypeNameSchema.optional(),
    valueMinor: valueMinorSchema.optional(),
    waReplyPayload: waReplyPayloadSchema.optional(),
  })
  .strict()
  .refine((body) => body.name !== undefined || body.valueMinor !== undefined || body.waReplyPayload !== undefined, {
    message: 'Send a name, a value or a WhatsApp button payload to change.',
  });
export type UpdateOutcomeTypeRequest = z.infer<typeof updateOutcomeTypeRequestSchema>;

export const outcomeTypeParamsSchema = z.object({ outcomeTypeId: uuidSchema }).strict();

/* -------------------------------------------------------------------------- */
/* Recording outcomes                                                          */
/* -------------------------------------------------------------------------- */

/** A typed count for a period: the declared tier. */
export const recordDeclaredOutcomeRequestSchema = z
  .object({
    outcomeTypeId: uuidSchema,
    periodStart: calendarDateSchema,
    periodEnd: calendarDateSchema,
    quantity: z.number().int().min(1, 'Record at least one.').max(1_000_000, 'That count is larger than Wend accepts.'),
  })
  .strict()
  .refine((body) => body.periodEnd >= body.periodStart, {
    path: ['periodEnd'],
    message: 'The period cannot end before it starts.',
  });
export type RecordDeclaredOutcomeRequest = z.infer<typeof recordDeclaredOutcomeRequestSchema>;

/** The non-file fields of a converter-list upload (multipart). */
export const uploadOutcomesRequestSchema = z.object({ outcomeTypeId: uuidSchema }).strict();
export type UploadOutcomesRequest = z.infer<typeof uploadOutcomesRequestSchema>;

export const outcomeUploadSchema = z
  .object({
    id: uuidSchema,
    outcomeTypeId: uuidSchema,
    filename: z.string(),
    rowsRead: z.number().int().min(0),
    rowsMatched: z.number().int().min(0),
    /**
     * Not recorded again: the same person, type and day as a conversion already
     * in the ledger or on an earlier row of this list. One conversion per person
     * per type per day is the ledger's rule (0042).
     */
    rowsDuplicate: z.number().int().min(0),
    rowsUnmatched: z.number().int().min(0),
    rowsInvalid: z.number().int().min(0),
    /** Slice 61: the earliest and latest day among the list's readable rows; null when it had none. */
    coversFrom: calendarDateSchema.nullable(),
    coversTo: calendarDateSchema.nullable(),
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type OutcomeUpload = z.infer<typeof outcomeUploadSchema>;

export const declaredOutcomeSchema = z
  .object({
    id: uuidSchema,
    outcomeTypeId: uuidSchema,
    periodStart: calendarDateSchema,
    periodEnd: calendarDateSchema,
    quantity: z.number().int().min(1),
    valueMinor: z.number().int().min(0),
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type DeclaredOutcome = z.infer<typeof declaredOutcomeSchema>;

/** How many recent uploads and declared entries the Outcomes page lists. */
export const OUTCOMES_RECENT_LIMIT = 20;

/** `GET /sense/outcomes`. */
export const outcomesOverviewSchema = z
  .object({
    types: z.array(outcomeTypeSchema),
    uploads: z.array(outcomeUploadSchema).max(OUTCOMES_RECENT_LIMIT),
    declared: z.array(declaredOutcomeSchema).max(OUTCOMES_RECENT_LIMIT),
  })
  .strict();
export type OutcomesOverview = z.infer<typeof outcomesOverviewSchema>;

/* -------------------------------------------------------------------------- */
/* The funnel                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Why a figure is absent, in the figure-state vocabulary the UI draws: an
 * absence somebody can fix by acting (`not_set_up` — no conversion defined,
 * tracking off) is a different message from one Wend is waiting on
 * (`not_measured` — no reports yet, email replies, replies waiting to be read).
 */
export const FUNNEL_ABSENCE_VALUES = ['not_measured', 'not_set_up'] as const;
export type FunnelAbsence = (typeof FUNNEL_ABSENCE_VALUES)[number];

/**
 * One stage's figure: a count of people, or a sentence saying why there is
 * none. **No field a number could live in on the unmeasured arm** — the same
 * discriminated shape `engagementPanelSchema` uses, so "not measured" can never
 * be drawn as a zero.
 */
export const funnelFigureSchema = z.discriminatedUnion('measured', [
  z.object({ measured: z.literal(true), people: z.number().int().min(0) }).strict(),
  z.object({ measured: z.literal(false), state: z.enum(FUNNEL_ABSENCE_VALUES), reason: z.string() }).strict(),
]);
export type FunnelFigure = z.infer<typeof funnelFigureSchema>;

export const FUNNEL_STAGE_VALUES = ['contacts', 'reached', 'engaged', 'responded', 'converted'] as const;
export type FunnelStage = (typeof FUNNEL_STAGE_VALUES)[number];

export const FUNNEL_STAGE_LABEL: Readonly<Record<FunnelStage, string>> = {
  contacts: 'Contacts',
  reached: 'Reached',
  engaged: 'Engaged',
  responded: 'Responded',
  converted: 'Converted',
};

/**
 * What Engaged counted, when it is measured. Opens are approximate — some mail
 * apps open every message, others block the image — so a figure that includes
 * them is drawn as an estimate; clicks only, or WhatsApp reads, are counts.
 */
export const FUNNEL_ENGAGED_BASIS_VALUES = ['opens_and_clicks', 'opens', 'clicks', 'reads'] as const;
export type FunnelEngagedBasis = (typeof FUNNEL_ENGAGED_BASIS_VALUES)[number];

export const funnelChannelSchema = z
  .object({
    channel: channelSchema,
    /** People with an address on this channel, now — not a windowed figure. Always measured. */
    contacts: z.number().int().min(0),
    reached: funnelFigureSchema,
    engaged: funnelFigureSchema,
    /** What Engaged counted; null when it is not measured. */
    engagedBasis: z.enum(FUNNEL_ENGAGED_BASIS_VALUES).nullable(),
    responded: funnelFigureSchema,
    /** Matched people whose conversion this channel earns (Wend-sent). */
    converted: funnelFigureSchema,
    /** What those attributed conversions were worth, paise; `null` when Converted is not measured. */
    convertedValueMinor: z.number().int().min(0).nullable(),
  })
  .strict();
export type FunnelChannel = z.infer<typeof funnelChannelSchema>;

/** `GET /sense/funnel`. */
export const funnelSchema = z
  .object({
    windowDays: z.literal(FUNNEL_WINDOW_DAYS),
    attributionDays: z.literal(ATTRIBUTION_WINDOW_DAYS),
    generatedAt: isoDateTimeSchema,
    /** Whether the workspace has declared any outcome type — without one, nothing converts. */
    outcomesDeclared: z.boolean(),
    channels: z.array(funnelChannelSchema).length(2),
    /**
     * Matched conversions in the window, attributed or not: people and value.
     * Null when Converted is not measured — the same test, so the tile and the
     * channel rows can never disagree about whether a zero is a zero.
     */
    matched: z.object({ people: z.number().int().min(0), valueMinor: z.number().int().min(0) }).strict().nullable(),
    /** Why there is no matched figure, as the channels' Converted rows say it; null when there is one. */
    convertedAbsence: z.object({ state: z.enum(FUNNEL_ABSENCE_VALUES), reason: z.string() }).strict().nullable(),
    /** Declared counts whose whole period lies in the window: quantity and value. Never added to matched. */
    declared: z.object({ quantity: z.number().int().min(0), valueMinor: z.number().int().min(0) }).strict().nullable(),
  })
  .strict();
export type Funnel = z.infer<typeof funnelSchema>;

/** The channel row a funnel speaks for, typed for a lookup. */
export type FunnelChannelName = z.infer<typeof channelSchema>;
